import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader } from "@/shared/ui/card";
import { Check, Star, Zap, Building } from "lucide-react";

const pricingPlans = [
  {
    name: "Starter",
    icon: Zap,
    price: "$299",
    period: "/month",
    description: "Perfect for small facilities and pilot projects",
    features: [
      "Up to 500 assets",
      "Basic OCR scanning",
      "AI assistant (50 queries/month)",
      "Standard reporting",
      "Email support",
      "Mobile app access",
      "Basic integrations"
    ],
    recommended: false,
    cta: "Start Trial"
  },
  {
    name: "Professional",
    icon: Star,
    price: "$899",
    period: "/month",
    description: "Ideal for growing enterprises with multiple properties",
    features: [
      "Up to 5,000 assets",
      "Advanced OCR + AI parsing",
      "Unlimited AI assistant",
      "Predictive maintenance",
      "Custom dashboards",
      "Priority support",
      "Advanced integrations",
      "Team collaboration tools",
      "Audit trails"
    ],
    recommended: true,
    cta: "Most Popular"
  },
  {
    name: "Enterprise",
    icon: Building,
    price: "Custom",
    period: "pricing",
    description: "For large organizations with complex requirements",
    features: [
      "Unlimited assets",
      "White-label solution",
      "Custom AI models",
      "Dedicated account manager",
      "On-premise deployment",
      "24/7 phone support",
      "Custom integrations",
      "Advanced security",
      "SLA guarantees",
      "Training & onboarding"
    ],
    recommended: false,
    cta: "Contact Sales"
  }
];

export function LandingPricing() {
  return (
    <section id="pricing" className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Star className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">Transparent Pricing</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Engineering-Grade Solutions
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose the plan that fits your facility's scale and complexity. 
            All plans include core AI functionality with no hidden fees.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <Card 
              key={index} 
              className={`relative border-2 transition-all duration-300 hover:shadow-elegant ${
                plan.recommended 
                  ? 'border-primary shadow-glow bg-gradient-accent' 
                  : 'border-border hover:border-primary/50 bg-card'
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold">
                    RECOMMENDED
                  </div>
                </div>
              )}
              
              <CardHeader className="text-center pb-6">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 mx-auto ${
                  plan.recommended ? 'bg-primary/20' : 'bg-primary/10'
                }`}>
                  <plan.icon className="h-6 w-6 text-primary" />
                </div>
                
                <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>
                <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>
                
                <div className="text-center">
                  <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-foreground text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  variant={plan.recommended ? "hero" : "engineering"} 
                  className="w-full"
                  size="lg"
                >
                  {plan.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-16">
          <div className="bg-gradient-accent rounded-2xl p-8 border border-primary/20">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              All Plans Include
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-sm">
              <div className="flex flex-col items-center gap-2">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <span className="font-medium text-foreground">30-Day Free Trial</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <span className="font-medium text-foreground">No Setup Fees</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <span className="font-medium text-foreground">Cancel Anytime</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <span className="font-medium text-foreground">99.9% Uptime SLA</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}